import { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
  useGetFoodDetailQuery,
  useGetFoodMetaDetailQuery,
  useSaveFoodMutation,
} from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import { MyFoodsDetailsFooterPage } from '@screens/MyFoodsDetailPage/MyFoodsDetailsFooterPage';
import { NutritionBadgeDetails } from '@/components/shared/NutritionBadgeDetails';
import { MyFoodsPageBasicDetails } from '@screens/MyFoodsDetailPage/MyFoodsPageBasicDetails';
import { MyFoodsPageCategoryDetails } from '@screens/MyFoodsDetailPage/MyFoodsPageCategoryDetails';
import NutritionStatusCard from '@design-system/Cards/NutritionStatusCard';
import { useParams, useLocation } from 'react-router-dom';
import { NavBar } from '@design-system/NavBar';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { BookmarkFlag } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { MyFoodDetailsSkeleton } from '@screens/MyFoodsDetailPage/MyFoodsDetailPageSkeleton';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { cn } from '@utils/styles';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';

interface ShowMoreMetaData {
  description: string;
  links?: string[] | [];
  title: string;
}

interface CardDescriptionInfoProps {
  data?: ShowMoreMetaData;
}

const CardDescriptionInfo = ({ data }: CardDescriptionInfoProps) => {
  const [displayLinks, setDisplayLinks] = useState<boolean>(false);
  const { description, links } = data || {};

  const onClickSupportingResearch = () => {
    if (links && links.length > 0) {
      window.open(links[0], '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={cn(
        'mt-5 flex flex-col justify-between',
        !displayLinks && 'h-60',
      )}
    >
      <ScrollArea>
        <span className="typography-body text-secondary">{description}</span>
        <ScrollBar />
      </ScrollArea>
      {(links && links.length > 0) && (
        <>
          {links.length > 1 ? (
            <div
              className="mt-2 flex flex-col justify-between cursor-pointer"
              onClick={() => setDisplayLinks(!displayLinks)}
            >
              <div className="flex justify-between items-end">
                <span className="typography-body text-primary">
                  Supporting research
                </span>
                {displayLinks ? (
                  <ChevronUp className="text-primary" />
                ) : (
                  <ChevronDown className="text-primary" />
                )}
              </div>
              {displayLinks && (
                <div className="flex gap-2 flex-col mt-3">
                  {links.map((link, index) => (
                    <div className="truncate my-3">
                      <a
                        key={`${index}-links`}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="typography-body text-primary truncate"
                      >
                        {link}
                      </a>
                      <hr className="border-stroke-primary" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              className="mt-2 flex cursor-pointer justify-between"
              onClick={onClickSupportingResearch}
            >
              <span className="typography-btn-large text-primary">
                Supporting research
              </span>
              <ChevronRight className="text-primary" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const NutritionFoodDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [bookmarkStatus, setBookmarkStatus] = useState<boolean>(
    location?.state?.isBookmarked,
  );

  const param = {
    food_id: Number(id),
  };

  const {
    data,
    isLoading: isFoodDetailsAPILoading,
    isError: isFoodDetailsAPIError,
  } = useGetFoodDetailQuery(param);

  const {
    about: aboutFood = {
      about_this_food: '',
      macronutrients: [],
      food_composition: '',
      how_to_consume: '',
    },
    badges = [],
    whyThisFood: categoryDetails = [],
    basic_details: basicDetails = {
      color: '',
      image_url: '',
      name: '',
      rank: '',
      servings: '',
    },
  } = data || {};

  const { data: showMoreData } = useGetFoodMetaDetailQuery(param);

  const { meta_data: showMoreMetaData = [] } = showMoreData || {};

  const handleOnSaveMutationSuccess = () => {
    setBookmarkStatus((previousValue) => !previousValue);
  };

  const { mutate, isPending } = useSaveFoodMutation({
    onSuccess: handleOnSaveMutationSuccess,
  });

  const handleOnBookmark = () => {
    const bookmarkFlag = bookmarkStatus
      ? BookmarkFlag.Remove
      : BookmarkFlag.Add;

    mutate({
      food_id: Number(id),
      flag: bookmarkFlag,
    });
  };

  if (isFoodDetailsAPILoading) {
    return <MyFoodDetailsSkeleton />;
  }

  if (isFoodDetailsAPIError) {
    return <ErrorLoadingPage variant="light" />;
  }

  return (
    <section>
      <div className="grid mx-auto gap-6 container p-4 md:px-8 lg:px-24 lg:py-6 max-w-[960px]">
        <NavBar className="md:-ml-5">
          <BookmarkButton
            bookmarked={bookmarkStatus}
            disabled={isPending}
            isPending={isPending}
            onClick={handleOnBookmark}
          />
        </NavBar>
        <div>
          <MyFoodsPageBasicDetails basicDetailsInfo={basicDetails} />
          {badges.length > 0 && (
            <div className="flex justify-center md:justify-start">
              <NutritionBadgeDetails badgeInfo={badges} />
            </div>
          )}
        </div>
        {categoryDetails?.length > 0 && (
          <>
            <div className="pt-6">
              <p className="typography-h1">
                Why it's a {basicDetails?.rank} for me
              </p>
              <p className="typography-btn-large pt-4">
                Here are the health systems where my body needs support
              </p>
            </div>
            <MyFoodsPageCategoryDetails categoryDetailsInfo={categoryDetails} />
          </>
        )}
        {showMoreMetaData?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {showMoreMetaData?.map((data: ShowMoreMetaData) => (
              <div key={data?.title} className="pt-5">
                <NutritionStatusCard
                  categoryTitle={data?.title}
                  cardDescription={<CardDescriptionInfo data={data} />}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {aboutFood && <MyFoodsDetailsFooterPage aboutFoodInfo={aboutFood} />}
    </section>
  );
};
